import * as React from 'react';
import { AlertTriangle, FileText, Sparkles } from 'lucide-react';
import { AreaChip } from '@/components/ui/area-chip';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import {
  Callout,
  CodeBadge,
  DetailCard,
  DetailHead,
  ListGroupLabel,
  ListItem,
  PartLabel,
  SegmentedControl,
  WorkspaceButton,
  WorkspaceInput,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import {
  DECLINE_PLACEMENT_REASONS,
  confirmInterviewProposal,
  declineInterviewProposal,
  type BulkPlacementResult,
  type DeclinePlacementReason,
  type InterviewProposal,
} from '@/lib/api/discovery-interview-proposals';
import { useDiscoveryWrite } from '@/lib/api/discovery-sessions';
import { PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { errorText } from './errors';

/**
 * Transcripts to place (T-0261; E23-design §5.2, §13). The interview finder
 * proposed where each transcript that arrived without an interview belongs:
 * attach to the interview its speakers are planned for, attach to a plan
 * row of its type that has no one named yet, or create a new one. The list
 * shows them above the interviews; one opens on the right
 * with Confirm, Change (the add form, pre-filled) and Decline. "Confirm all"
 * takes the ones the server pre-selected (high confidence) to the bulk
 * endpoint. Everything shown was decided by code on the server; this screen
 * words it. Reuses the sessions list items, the detail head and card, the
 * callout and the workspace buttons.
 */

/** The Proposed line of a placement. */
export function proposedText(p: InterviewProposal): string {
  if (p.placement === 'attach' && p.interview) {
    const { code, title, status } = p.interview;
    if (p.match_basis === 'type_plan_row') {
      return `Attach to ${code} ${title} (same type, no one named yet)`;
    }
    const version = p.version_kind === 'corrected' ? ', as a corrected version' : '';
    return `Attach to ${code} ${title} (${status})${version}`;
  }
  return `New interview · type ${p.interview_type?.name ?? 'Other'}`;
}

/** The list chip of a placement. */
export function placementChip(p: InterviewProposal): { label: string; tone: StatusChipTone } {
  if (p.document.stale) return { label: 'Newer version', tone: 'at-risk' };
  if (p.placement === 'attach' && p.interview) {
    return {
      label: `Attach to ${p.interview.code}`,
      tone: p.preselected ? 'healthy' : 'amber',
    };
  }
  return { label: 'New interview', tone: 'neutral' };
}

const REASON_LABEL: Record<DeclinePlacementReason, string> = {
  not_an_interview: 'Not an interview',
  duplicate: 'A duplicate',
  other: 'Other',
};

/** The batch's outcome in one line for the toast. */
export function bulkOutcomeText(result: BulkPlacementResult): string {
  const codes = result.items
    .filter((i) => i.outcome === 'confirmed')
    .map((i) => (i.outcome === 'confirmed' ? i.result.interview.code : ''))
    .filter(Boolean);
  const placed =
    result.confirmed === 0
      ? 'Nothing was placed'
      : `Placed ${plural(result.confirmed, 'transcript')} (${codes.join(', ')}); the readings start now`;
  const skipped = result.skipped > 0 ? `. ${plural(result.skipped, 'placement')} skipped` : '';
  return `${placed}${skipped}.`;
}

export function PlacementListItems({
  proposals,
  selected,
  onPick,
  onConfirmAll,
  confirmingAll,
}: {
  proposals: ReadonlyArray<InterviewProposal>;
  selected: string | null;
  onPick: (proposalId: string) => void;
  onConfirmAll: (ids: string[]) => void;
  confirmingAll: boolean;
}) {
  if (proposals.length === 0) return null;
  const sure = proposals
    .filter((p) => p.preselected && !p.document.stale)
    .map((p) => p.proposal_id);
  return (
    <div>
      <ListGroupLabel
        count={proposals.length}
        action={
          sure.length > 0 ? (
            <button
              type="button"
              className={linkishClass()}
              disabled={confirmingAll}
              onClick={() => onConfirmAll(sure)}
            >
              {confirmingAll ? 'Confirming…' : `Confirm all (${sure.length})`}
            </button>
          ) : undefined
        }
      >
        Transcripts to place
      </ListGroupLabel>
      {proposals.map((p) => {
        const chip = placementChip(p);
        return (
          <ListItem
            key={p.proposal_id}
            selected={selected === p.proposal_id}
            onClick={() => onPick(p.proposal_id)}
            leading={<FileText className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />}
            title={p.document.title}
            meta={p.match_text}
            trailing={
              <StatusChip tone={chip.tone} size="sm">
                {chip.label}
              </StatusChip>
            }
          />
        );
      })}
    </div>
  );
}

export function PlacementDetail({
  engagementId,
  proposal,
  onConfirmed,
  onChange,
  onDeclined,
}: {
  engagementId: string;
  proposal: InterviewProposal;
  onConfirmed: (interviewId: string) => void;
  onChange: () => void;
  onDeclined: () => void;
}) {
  const { toast } = useToast();
  const [declining, setDeclining] = React.useState(false);
  const [reason, setReason] = React.useState<DeclinePlacementReason>('not_an_interview');
  const [text, setText] = React.useState('');

  const confirm = useDiscoveryWrite(engagementId, () =>
    confirmInterviewProposal(engagementId, proposal.proposal_id),
  );
  const decline = useDiscoveryWrite(engagementId, () =>
    declineInterviewProposal(engagementId, proposal.proposal_id, {
      reason,
      ...(text.trim() ? { text: text.trim() } : {}),
    }),
  );

  const onConfirm = () =>
    confirm.mutate(undefined, {
      onSuccess: (r) => {
        toast({
          message: `${proposal.document.title} is ${r.interview.created ? 'now' : 'attached to'} ${r.interview.code} ${r.interview.title}: the reading starts now.`,
        });
        onConfirmed(r.interview.id);
      },
      onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
    });
  const onDecline = () =>
    decline.mutate(undefined, {
      onSuccess: () => {
        toast({ message: `${proposal.document.title} stays a document.` });
        onDeclined();
      },
      onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
    });

  const attach = proposal.placement === 'attach' && proposal.interview;
  const busy = confirm.isPending || decline.isPending;
  return (
    <div>
      <DetailHead
        leading={
          attach ? (
            <CodeBadge size="lg">{proposal.interview!.code}</CodeBadge>
          ) : (
            <FileText className="h-7 w-7 flex-none text-ink-500" aria-hidden="true" />
          )
        }
        title={proposal.document.title}
        meta={`A transcript to place · ${plural(proposal.speaker_count, 'speaker')} · ${plural(proposal.line_count, 'line')}`}
      />
      {proposal.document.stale ? (
        <Callout tone="warn" icon={AlertTriangle} className="mb-3">
          A newer version of this file was uploaded since. Caliber proposes again for the new
          version; decline this one.
        </Callout>
      ) : null}
      <Callout tone="ai" icon={Sparkles} className="mb-3">
        {proposal.match_text}
      </Callout>
      <DetailCard className="flex-col gap-3.5">
        <div>
          <PartLabel>Proposed</PartLabel>
          <div className="text-body-sm text-ink-900">{proposedText(proposal)}</div>
        </div>
        <div>
          <PartLabel>Areas</PartLabel>
          {proposal.areas.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {proposal.areas.map((a) => (
                <AreaChip key={a.id} title={a.label}>
                  {a.label}
                </AreaChip>
              ))}
            </div>
          ) : (
            <div className="text-meta text-ink-500">
              {attach ? "The interview's own." : 'None yet. Change the placement to pick them.'}
            </div>
          )}
        </div>
        <div>
          <PartLabel>People</PartLabel>
          {proposal.people.length === 0 ? (
            <div className="text-meta text-ink-500">
              Nobody matched yet. The reading maps the speakers to people.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {proposal.people.map((p) => (
                <li key={p.speaker_label} className="flex items-center gap-2.5 text-body-sm">
                  <PersonAvatar id={p.person_id ?? p.speaker_label} name={p.full_name} size={24} />
                  <span className="font-medium text-ink-900">{p.full_name}</span>
                  <span className="text-meta text-ink-500">
                    {p.title ? `${p.title} · ` : ''}
                    {p.person_id ? 'already in this engagement' : 'new'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DetailCard>
      {declining ? (
        <Callout
          tone="form"
          className="mb-3"
          role="group"
          actions={
            <>
              <WorkspaceButton
                variant="destructive"
                disabled={busy || (reason === 'other' && !text.trim())}
                onClick={onDecline}
              >
                Decline
              </WorkspaceButton>
              <WorkspaceButton variant="tertiary" onClick={() => setDeclining(false)}>
                Keep
              </WorkspaceButton>
            </>
          }
        >
          <div className="mb-2 font-medium text-ink-900">Why is it not placed?</div>
          <SegmentedControl
            aria-label="Why it is declined"
            value={reason}
            onChange={setReason}
            options={DECLINE_PLACEMENT_REASONS.map((r) => ({ value: r, label: REASON_LABEL[r] }))}
          />
          {reason === 'other' ? (
            <WorkspaceInput
              aria-label="A few words"
              className="mt-2"
              placeholder="A few words"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          ) : null}
        </Callout>
      ) : null}
      <div className="flex justify-end gap-2">
        <WorkspaceButton
          variant="tertiary"
          size="md"
          disabled={busy}
          onClick={() => setDeclining(true)}
        >
          Decline
        </WorkspaceButton>
        <WorkspaceButton variant="secondary" size="md" disabled={busy} onClick={onChange}>
          Change
        </WorkspaceButton>
        <WorkspaceButton
          variant="primary"
          size="md"
          disabled={busy || proposal.document.stale}
          onClick={onConfirm}
        >
          {confirm.isPending ? 'Confirming…' : 'Confirm'}
        </WorkspaceButton>
      </div>
    </div>
  );
}
